import { Component } from '@theme/component';

/**
 * Lightweight scroll-snap product carousel for the "Pick your next" section.
 *
 * Prev/next arrows scroll the track by roughly one card width. Arrow disabled
 * state tracks the scroll position so the shopper can see when they've reached
 * either end.
 */
class HpCarousel extends Component {
  #dragging = false;

  /**
   * Track width changes without a window resize — images finishing loading, a font swap,
   * the theme editor resizing the section — and each one invalidates the arrows, the
   * scrollbar thumb and the edge fade. <scroll-hint> observes for the same reason.
   * @type {ResizeObserver}
   */
  #resizeObserver = new ResizeObserver(() => this.#onScroll());

  connectedCallback() {
    super.connectedCallback();
    this.#track?.addEventListener('scroll', this.#onScroll, { passive: true });
    this.#scrollbar?.addEventListener('pointerdown', this.#onPointerDown);
    window.addEventListener('resize', this.#onScroll, { passive: true });
    if (this.#track) this.#resizeObserver.observe(this.#track);
    this.#onScroll();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#track?.removeEventListener('scroll', this.#onScroll);
    this.#scrollbar?.removeEventListener('pointerdown', this.#onPointerDown);
    window.removeEventListener('resize', this.#onScroll);
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
    this.#resizeObserver.disconnect();
  }

  /** @returns {HTMLElement | null} */
  get #track() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="track"]'));
  }

  /** @returns {HTMLElement | null} */
  get #arrows() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="arrows"]'));
  }

  /** @returns {HTMLButtonElement | null} */
  get #prev() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="prev"]'));
  }

  /** @returns {HTMLButtonElement | null} */
  get #next() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="next"]'));
  }

  /** @returns {HTMLElement | null} */
  get #scrollbar() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="scrollbar"]'));
  }

  /** @returns {HTMLElement | null} */
  get #thumb() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[ref="thumb"]'));
  }

  /** Maximum horizontal scroll distance of the track. */
  get #maxScroll() {
    const track = this.#track;
    return track ? Math.max(0, track.scrollWidth - track.clientWidth) : 0;
  }

  /** Distance to scroll per arrow click: one slide plus the track gap. */
  get #step() {
    const track = this.#track;
    if (!track) return 0;
    const slide = /** @type {HTMLElement | null} */ (track.querySelector('.hp-carousel__slide'));
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || '0') || 0;
    const slideWidth = slide?.getBoundingClientRect().width ?? track.clientWidth;
    return slideWidth + gap;
  }

  scrollPrev() {
    this.#track?.scrollBy({ left: -this.#step, behavior: 'smooth' });
  }

  scrollNext() {
    this.#track?.scrollBy({ left: this.#step, behavior: 'smooth' });
  }

  /** Runs on scroll/resize: keep the arrows and scrollbar in sync. */
  #onScroll = () => {
    this.#updateArrows();
    this.#updateScrollbar();
  };

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;

    // All cards fit in the viewport → nothing to page through, hide the arrows.
    const arrows = this.#arrows;
    const scrollable = track.scrollWidth > track.clientWidth + 1;
    if (arrows) arrows.hidden = !scrollable;

    // 1px tolerance guards against sub-pixel scroll rounding.
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

    if (this.#prev) this.#prev.disabled = atStart;
    if (this.#next) this.#next.disabled = atEnd;
  };

  /** Reflect the track's scroll position onto the custom scrollbar thumb. */
  #updateScrollbar = () => {
    const track = this.#track;
    const scrollbar = this.#scrollbar;
    const thumb = this.#thumb;
    if (!track || !scrollbar || !thumb) return;

    // Nothing to scroll → hide the whole bar (e.g. fewer cards than fit).
    const scrollable = track.scrollWidth > track.clientWidth + 1;
    scrollbar.hidden = !scrollable;
    if (!scrollable) return;

    const barWidth = scrollbar.clientWidth;
    const thumbWidth = Math.max(24, barWidth * (track.clientWidth / track.scrollWidth));
    const maxTravel = barWidth - thumbWidth;
    const progress = this.#maxScroll ? track.scrollLeft / this.#maxScroll : 0;

    thumb.style.width = `${thumbWidth}px`;
    thumb.style.transform = `translateX(${progress * maxTravel}px)`;
  };

  /** Map a pointer x within the bar to a track scroll position. */
  #scrollToPointer(clientX) {
    const track = this.#track;
    const scrollbar = this.#scrollbar;
    const thumb = this.#thumb;
    if (!track || !scrollbar || !thumb) return;
    const rect = scrollbar.getBoundingClientRect();
    const thumbWidth = thumb.getBoundingClientRect().width;
    const maxTravel = rect.width - thumbWidth;
    if (maxTravel <= 0) return;
    const offset = clientX - rect.left - thumbWidth / 2;
    const progress = Math.min(1, Math.max(0, offset / maxTravel));
    track.scrollLeft = progress * this.#maxScroll;
  }

  #onPointerDown = (event) => {
    if (!this.#track) return;
    event.preventDefault();
    this.#dragging = true;
    this.#scrollbar?.setPointerCapture?.(event.pointerId);
    this.#scrollToPointer(event.clientX);
    window.addEventListener('pointermove', this.#onPointerMove);
    window.addEventListener('pointerup', this.#onPointerUp);
  };

  #onPointerMove = (event) => {
    if (this.#dragging) this.#scrollToPointer(event.clientX);
  };

  #onPointerUp = () => {
    this.#dragging = false;
    window.removeEventListener('pointermove', this.#onPointerMove);
    window.removeEventListener('pointerup', this.#onPointerUp);
  };
}

if (!customElements.get('hp-carousel')) {
  customElements.define('hp-carousel', HpCarousel);
}
